/**
 * What one keydown means to the shortcut recorder (plan D3/D5). Pure, so the
 * Mod rule, the typing guard and the reserved-chord refusal are testable per
 * platform without a DOM.
 */
import { eventToChord, logicalEventKeyToken, normalizeChord, type KeyboardEventLike } from '../model/chord';
import { findReservedChord, isScopedReservedException } from '../model/reservedChords';
import type {
  ChordModifier,
  KeybindingCommandId,
  KeyboardLayoutSnapshot,
  Platform,
} from '../model/keybindingTypes';
import { formattedChord } from '../hooks/useShortcutLabel';
import { reservedByText } from './keybindingDisplay';

export type RecorderRejection = 'typing' | 'reserved' | 'unsupported';

export type RecorderOutcome =
  | { readonly kind: 'ignore' }
  | { readonly kind: 'cancel' }
  | { readonly kind: 'clear' }
  | { readonly kind: 'reject'; readonly reason: RecorderRejection; readonly message: string }
  | { readonly kind: 'chord'; readonly chord: string };

export type RecorderContext = {
  readonly commandId: KeybindingCommandId;
  readonly platform: Platform;
  readonly isDev: boolean;
  readonly layout: KeyboardLayoutSnapshot;
};

const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'OS']);

/**
 * Orchestrator ruling: the platform's primary modifier (Cmd on macOS, Ctrl on
 * Windows/Linux) is stored as `Mod`, so an exported bundle works on every
 * platform. A literal `Ctrl` survives only on macOS, where it is a distinct
 * modifier; a Windows/Linux Super key stays `Cmd` and is refused as an OS chord.
 */
export function toPortableModifiers(
  modifiers: readonly ChordModifier[],
  targetPlatform: Platform,
): ChordModifier[] {
  const primary: ChordModifier = targetPlatform === 'mac' ? 'Cmd' : 'Ctrl';
  return modifiers.map((modifier) => (modifier === primary ? 'Mod' : modifier));
}

function typingMessage(targetPlatform: Platform): string {
  const modifiers = targetPlatform === 'mac' ? '⌘, ⌃ or ⌥' : 'Ctrl or Alt';
  return `Add ${modifiers}, or use a function key. Plain keys and Shift+key type text.`;
}

const reject = (reason: RecorderRejection, message: string): RecorderOutcome => ({ kind: 'reject', reason, message });

const hasCommandModifier = (event: KeyboardEventLike): boolean => event.ctrlKey || event.metaKey || event.altKey;

export function interpretRecorderKeydown(event: KeyboardEventLike, context: RecorderContext): RecorderOutcome {
  if (MODIFIER_KEYS.has(event.key)) return { kind: 'ignore' };
  // D5: dead keys, composition and AltGr produce characters; never chords.
  // Checked before Escape/Backspace, so a composing Backspace never clears a
  // shortcut and a composing Escape never cancels.
  if (event.isComposing || event.key === 'Dead' || event.getModifierState?.('AltGraph')) {
    return reject('unsupported', 'That key types a character on your keyboard layout. Choose another key.');
  }
  const bare = !hasCommandModifier(event) && !event.shiftKey;
  if (bare && event.key === 'Escape') return { kind: 'cancel' };
  if (bare && event.key === 'Backspace') return { kind: 'clear' };

  const recorded = eventToChord(event, context.layout);
  if (!recorded) {
    const typingShaped = !hasCommandModifier(event) &&
      (event.key.length === 1 || logicalEventKeyToken(event.key) !== null);
    return typingShaped
      ? reject('typing', typingMessage(context.platform))
      : reject('unsupported', 'That key cannot be used in a shortcut.');
  }

  const chord = normalizeChord({ ...recorded, modifiers: toPortableModifiers(recorded.modifiers, context.platform) });
  if (!chord) return reject('typing', typingMessage(context.platform));

  const reserved = findReservedChord(chord, context.platform, context.isDev, context.layout);
  if (reserved && !isScopedReservedException(context.commandId, chord, context.platform, context.isDev)) {
    const label = formattedChord(chord, context.platform, context.layout);
    return reject('reserved', reserved.layoutDependent
      ? `${label} can match a reserved shortcut on your keyboard layout. Choose another key.`
      : `${label} is ${reservedByText(reserved.role)}.`);
  }
  return { kind: 'chord', chord };
}
